import React from "react";

// ============================================================
// FinanceFilterPanel — right-side slide-in panel used by both
// the Giving and Expenses tabs.
//
// Design constraints (from Phase 4c spec):
//  • Overlay + panel are position:fixed on the right.
//  • Panel max-height: calc(100vh - 64px) so it never overlaps
//    the bottom navigation bar on mobile.
//  • Panel body scrolls independently (overflow-y: auto).
//  • Values update `filters` LIVE via onChange — no "apply" step.
//
// The panel is fully controlled: parent owns `filters` state, the
// panel just renders inputs and emits patches through `onChange`.
// ============================================================

const GOLD = "var(--kc-accent)";

export default function FinanceFilterPanel({
  open,
  onClose,
  title = "Filters",
  filters,
  onChange,
  onClear,
  schema,   // array of field descriptors — see below
  testidPrefix = "finance-filter",
}) {
  if (!open) return null;

  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <>
      {/* Overlay */}
      <div
        role="button"
        aria-label="Close filter panel"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => (e.key === "Escape" || e.key === "Enter") && onClose()}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.55)", zIndex: 60,
        }}
        data-testid={`${testidPrefix}-overlay`}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={`${testidPrefix}-panel`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(100%, 380px)",
          maxHeight: "calc(100vh - 64px)",
          overflowY: "auto",
          background: "var(--kc-surface)",
          borderLeft: `1px solid ${GOLD}44`,
          boxShadow: "-6px 0 24px rgba(0,0,0,0.6)",
          zIndex: 61,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header (sticky) */}
        <div
          style={{
            position: "sticky", top: 0, background: "var(--kc-surface)",
            padding: "14px 16px",
            borderBottom: `1px solid ${GOLD}22`,
            display: "flex", alignItems: "center", gap: 10, zIndex: 1,
          }}
        >
          <div style={{
            flex: 1, fontFamily: "'Cinzel',serif",
            fontSize: 15, fontWeight: 700, color: "#fff",
            letterSpacing: "0.06em", textTransform: "uppercase",
          }}>
            {title}
          </div>
          <button
            className="btn btn-sm btn-outline"
            onClick={onClear}
            data-testid={`${testidPrefix}-clear-all`}
            title="Clear all filters"
          >
            Clear All
          </button>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            data-testid={`${testidPrefix}-close`}
            style={{
              background: "transparent", border: "none",
              color: "#fff", fontSize: 22, lineHeight: 1,
              cursor: "pointer", padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
          {schema.map(field => (
            <FieldRow
              key={field.key}
              field={field}
              value={filters[field.key]}
              setValue={(v) => set({ [field.key]: v })}
              testidPrefix={testidPrefix}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

// -------------------------------------------------------------
// Field row — dispatches to the right input type from schema.
// Supported types: dateRange, text, multiSelect, checkbox, amountRange.
// -------------------------------------------------------------
function FieldRow({ field, value, setValue, testidPrefix }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontSize: 11, fontWeight: 700, color: "var(--kc-sub)",
          textTransform: "uppercase", letterSpacing: 1,
        }}
      >
        {field.label}
      </label>
      {field.type === "dateRange" && (
        <DateRangeInput value={value} onChange={setValue} testid={`${testidPrefix}-${field.key}`} />
      )}
      {field.type === "text" && (
        <input
          type="text"
          className="form-input"
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`${testidPrefix}-${field.key}`}
        />
      )}
      {field.type === "multiSelect" && (
        <MultiSelect
          options={field.options}
          selected={Array.isArray(value) ? value : []}
          onChange={setValue}
          testid={`${testidPrefix}-${field.key}`}
        />
      )}
      {field.type === "checkbox" && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => setValue(e.target.checked)}
            data-testid={`${testidPrefix}-${field.key}`}
          />
          <span style={{ fontSize: 13, color: "var(--kc-text)" }}>{field.checkboxLabel || field.label}</span>
        </label>
      )}
      {field.type === "amountRange" && (
        <AmountRangeInput
          value={value}
          onChange={setValue}
          currencySymbol={field.currencySymbol || "$"}
          testid={`${testidPrefix}-${field.key}`}
        />
      )}
    </div>
  );
}

function DateRangeInput({ value, onChange, testid }) {
  const v = value || { from: "", to: "" };
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="date"
        className="form-input"
        value={v.from || ""}
        onChange={(e) => onChange({ ...v, from: e.target.value })}
        data-testid={`${testid}-from`}
        style={{ flex: 1 }}
      />
      <span style={{ opacity: 0.5, fontSize: 12 }}>to</span>
      <input
        type="date"
        className="form-input"
        value={v.to || ""}
        onChange={(e) => onChange({ ...v, to: e.target.value })}
        data-testid={`${testid}-to`}
        style={{ flex: 1 }}
      />
    </div>
  );
}

function AmountRangeInput({ value, onChange, currencySymbol, testid }) {
  const v = value || { min: "", max: "" };
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        className="form-input"
        placeholder={`${currencySymbol} min`}
        value={v.min ?? ""}
        onChange={(e) => onChange({ ...v, min: e.target.value })}
        data-testid={`${testid}-min`}
        style={{ flex: 1 }}
      />
      <span style={{ opacity: 0.5, fontSize: 12 }}>to</span>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        className="form-input"
        placeholder={`${currencySymbol} max`}
        value={v.max ?? ""}
        onChange={(e) => onChange({ ...v, max: e.target.value })}
        data-testid={`${testid}-max`}
        style={{ flex: 1 }}
      />
    </div>
  );
}

function MultiSelect({ options, selected, onChange, testid }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(x => x !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid={testid}>
      {options.map(opt => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            data-testid={`${testid}-opt-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: `1px solid ${active ? GOLD : "color-mix(in srgb, var(--kc-accent) 25%, transparent)"}`,
              background: active ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "transparent",
              color: active ? GOLD : "rgba(255,255,255,0.75)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
